/**
 * SSO (Single Sign-On) Service for Frontend
 * WebSocket을 통해 Admin과 Frontend 간의 인증 상태를 동기화합니다.
 */

import { WEBSOCKET_CONFIG } from '../config';

interface User {
  user_id: string;
  name: string;
  email?: string;
  phone?: string;
  role: string;
  status: string;
  created_at: string;
}

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  accessToken: string | null;
}

type AuthListener = (authState: AuthState) => void;

class SSOService {
  private static instance: SSOService;
  private websocket: WebSocket | null = null;
  private listeners: Set<AuthListener> = new Set();
  private currentAuthState: AuthState = {
    isAuthenticated: false,
    user: null,
    accessToken: null
  };
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 3000;
  private seqCounter = 1;
  private uniqueId: string;

  private constructor() {
    console.log('🚀 [Frontend SSO] Initializing WebSocket SSO service...');
    this.uniqueId = '';
    this.initializeAuthState();
    this.requestUniqueId().then(() => {
      this.connectWebSocket();
    });
    console.log('✅ [Frontend SSO] SSO service initialized');
  }

  public static getInstance(): SSOService {
    if (!SSOService.instance) {
      SSOService.instance = new SSOService();
    }
    return SSOService.instance;
  }

  /**
   * 초기 인증 상태 설정
   */
  private initializeAuthState(): void {
    const token = localStorage.getItem('access_token');
    const userStr = localStorage.getItem('user');

    if (token && userStr) {
      try {
        const user = JSON.parse(userStr);
        this.currentAuthState = {
          isAuthenticated: true,
          user,
          accessToken: token
        };
      } catch (error) {
        console.error('Failed to parse stored user data:', error);
        this.clearAuthData();
      }
    }
  }

  /**
   * WebSocket 연결 설정
   */
  private connectWebSocket(): void {
    try {
      // config.ts의 WEBSOCKET_CONFIG 사용
      const wsUrl = WEBSOCKET_CONFIG.URL;
      console.log('🔌 [Frontend SSO] Connecting to WebSocket:', wsUrl);

      this.websocket = new WebSocket(wsUrl);

      this.websocket.onopen = () => {
        console.log('✅ [Frontend SSO] WebSocket connected successfully');
        this.reconnectAttempts = 0;

        // 연결되면 동기화 요청 전송
        this.sendWebSocketMessage('sso_sync_request', {});
      };

      this.websocket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          console.log('📨 [Frontend SSO] WebSocket message received:', message);

          const { action, data } = message;

          switch (action) {
            case 'sso_login':
              this.handleAuthLogin(data);
              break;
            case 'sso_logout':
              this.handleAuthLogout();
              break;
            case 'sso_refresh':
              this.handleAuthRefresh(data);
              break;
            case 'sso_sync_request':
              this.handleSyncRequest();
              break;
            default:
              console.log('🔔 [Frontend SSO] Unhandled WebSocket message:', action);
          }
        } catch (error) {
          console.error('❌ [Frontend SSO] Error parsing WebSocket message:', error);
        }
      };

      this.websocket.onclose = () => {
        console.log('🔌 [Frontend SSO] WebSocket connection closed');
        this.websocket = null;
        this.scheduleReconnect();
      };

      this.websocket.onerror = (error) => {
        console.error('❌ [Frontend SSO] WebSocket error:', error);
      };

    } catch (error) {
      console.error('❌ [Frontend SSO] Failed to create WebSocket connection:', error);
      this.scheduleReconnect();
    }
  }

  /**
   * WebSocket 재연결 스케줄링
   */
  private scheduleReconnect(): void {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      console.log(`🔄 [Frontend SSO] Scheduling reconnect attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${this.reconnectDelay}ms`);

      setTimeout(() => {
        this.connectWebSocket();
      }, this.reconnectDelay);

      // 재연결 간격을 점진적으로 증가
      this.reconnectDelay = Math.min(this.reconnectDelay * 1.5, 30000);
    } else {
      console.error('❌ [Frontend SSO] Max reconnection attempts reached');
    }
  }

  /**
   * WebSocket으로 메시지 전송
   */
  private sendWebSocketMessage(action: string, data: any): void {
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      const message = {
        action,
        seq: this.generateSequenceId(),
        ts: this.getCurrentTimestamp(),
        data
      };

      console.log('📤 [Frontend SSO] Sending WebSocket message:', message);
      this.websocket.send(JSON.stringify(message));
    } else {
      console.warn('⚠️ [Frontend SSO] WebSocket not connected, message not sent:', action);
    }
  }

  /**
   * 백엔드로부터 고유 ID 요청
   */
  private async requestUniqueId(): Promise<void> {
    try {
      console.log('🔑 [Frontend SSO] Requesting unique ID from backend...');
      
      const response = await fetch('/api/unique-id/generate', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      this.uniqueId = data.unique_id;
      
      console.log(`✅ [Frontend SSO] Received unique ID: ${this.uniqueId}`);
      
    } catch (error) {
      console.error('❌ [Frontend SSO] Failed to get unique ID from backend:', error);
      // 백엔드 요청 실패 시 로컬 생성으로 폴백
      this.uniqueId = this.generateFallbackUniqueId();
      console.log(`🔄 [Frontend SSO] Using fallback unique ID: ${this.uniqueId}`);
    }
  }

  /**
   * 폴백용 고유 ID 생성 (백엔드 요청 실패 시)
   */
  private generateFallbackUniqueId(): string {
    const timestamp = Date.now().toString().slice(-4);
    const random = Math.random().toString(36).substring(2, 6).toUpperCase();
    return `${timestamp}${random}`;
  }

  /**
   * 시퀀스 ID 생성 (고유 ID + 순차적 기반)
   */
  private generateSequenceId(): string {
    const seq = this.seqCounter.toString().padStart(5, '0');
    this.seqCounter++;
    
    // 5자리 rotation (00000 ~ 99999)
    if (this.seqCounter > 99999) {
      this.seqCounter = 0;
    }
    
    return `F${this.uniqueId}-${seq}`;
  }

  /**
   * 현재 타임스탬프 생성
   */
  private getCurrentTimestamp(): string {
    return new Date().toISOString().replace('T', ' ').slice(0, 23);
  }

  /**
   * 로그인 처리
   */
  private handleAuthLogin(payload: { user: User; accessToken: string }): void {
    const { user, accessToken } = payload;

    // 로컬 스토리지 업데이트
    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('user', JSON.stringify(user));

    // 상태 업데이트
    this.currentAuthState = {
      isAuthenticated: true,
      user,
      accessToken
    };

    // 리스너들에게 알림
    this.notifyListeners();
  }

  /**
   * 로그아웃 처리
   */
  private handleAuthLogout(): void {
    this.clearAuthData();
    this.notifyListeners();
  }

  /**
   * 토큰 갱신 처리
   */
  private handleAuthRefresh(payload: { accessToken: string }): void {
    const { accessToken } = payload;

    localStorage.setItem('access_token', accessToken);
    this.currentAuthState.accessToken = accessToken;

    this.notifyListeners();
  }

  /**
   * 동기화 요청 처리
   */
  private handleSyncRequest(): void {
    // 현재 상태를 요청자에게 전송
    if (this.currentAuthState.isAuthenticated) {
      this.broadcastMessage('AUTH_LOGIN', {
        user: this.currentAuthState.user,
        accessToken: this.currentAuthState.accessToken
      });
    }
  }

  /**
   * 인증 데이터 초기화
   */
  private clearAuthData(): void {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');

    this.currentAuthState = {
      isAuthenticated: false,
      user: null,
      accessToken: null
    };
  }

  /**
   * WebSocket으로 메시지 브로드캐스트
   */
  private broadcastMessage(action: string, data?: any): void {
    console.log('📡 [Frontend SSO] Broadcasting message via WebSocket:', { action, data: data ? 'present' : 'none' });
    this.sendWebSocketMessage(action, data);
  }

  /**
   * 리스너들에게 상태 변경 알림
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      try {
        listener(this.currentAuthState);
      } catch (error) {
        console.error('Error notifying auth listener:', error);
      }
    });
  }

  // Public API

  /**
   * 현재 인증 상태 반환
   */
  public getAuthState(): AuthState {
    return { ...this.currentAuthState };
  }

  /**
   * 인증 상태 리스너 추가
   */
  public addAuthListener(listener: AuthListener): void {
    this.listeners.add(listener);
  }

  /**
   * 인증 상태 리스너 제거
   */
  public removeAuthListener(listener: AuthListener): void {
    this.listeners.delete(listener);
  }

  /**
   * 로그인 상태 브로드캐스트
   */
  public broadcastLogin(user: User, accessToken: string): void {
    console.log('🔊 [Frontend SSO] Broadcasting login:', { user: user.user_id, accessToken: accessToken.substring(0, 10) + '...' });
    this.handleAuthLogin({ user, accessToken });
    this.broadcastMessage('sso_login', { user, accessToken });
  }

  /**
   * 로그아웃 상태 브로드캐스트
   */
  public broadcastLogout(): void {
    this.handleAuthLogout();
    this.broadcastMessage('sso_logout');
  }

  /**
   * 토큰 갱신 브로드캐스트
   */
  public broadcastTokenRefresh(accessToken: string): void {
    this.handleAuthRefresh({ accessToken });
    this.broadcastMessage('sso_refresh', { accessToken });
  }

  /**
   * 다른 탭에서 인증 상태 동기화 요청
   */
  public requestSync(): void {
    this.broadcastMessage('sso_sync_request');
  }

  /**
   * 액세스 토큰 반환
   */
  public getAccessToken(): string | null {
    return this.currentAuthState.accessToken;
  }

  /**
   * 사용자 정보 반환
   */
  public getUser(): User | null {
    return this.currentAuthState.user;
  }

  /**
   * 인증 여부 확인
   */
  public isAuthenticated(): boolean {
    return this.currentAuthState.isAuthenticated;
  }

  /**
   * 서비스 정리
   */
  public destroy(): void {
    if (this.websocket) {
      this.websocket.close();
      this.websocket = null;
    }
    this.listeners.clear();
  }
}

export default SSOService;
export type { User, AuthState, AuthListener };